/**
 * `<page-policies>` — the policy list, routed at `/instances/:id/policies`.
 *
 * Implements `SC-F526` (list, search, filter by kind), the list half of `SC-F527`
 * (a shadow policy is distinguishable from an enforcing one at a glance), the list
 * half of `SC-F529` (a flagged row links to the rule, and the draft can be linted
 * on demand with the error / blocking-warning split spelled out) and `SC-S509`'s
 * first criterion — the default-deny notice, which is the single most important
 * sentence this surface renders.
 *
 * **Rows arrive in snapshot order and are never re-sorted here.** Priority
 * descending, then name ascending, is the order the engine evaluates in, and every
 * "first wins" rule downstream inherits it: masks never stack and the first
 * candidate to claim a column wins, row filters fold in that order, and a tie
 * between two policies at the same priority is broken by name. A list sorted
 * alphabetically would hide the tie-break that decides the outcome.
 *
 * Stories: `SC-S509`, `SC-S521`, `SC-S522`, `SC-S523`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
// `SC-F825`, mounted below the list: the report reads the same objects and answers the
// question the list raises.
import "./access-review.js";
import "./policy-outcome.js";
import {
  enforcementModeVariant,
  type BnBadgeVariant,
  type BnColumn,
  type BnPageChangeDetail,
  type BnRow,
  type BnRowClickDetail,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  POLICY_KINDS,
  listPolicies,
  lintDraft,
  type EnforcementMode,
  type PolicyFinding,
  type PolicyKind,
  type PolicyList,
  type PolicyNotice,
  type PolicyRow,
} from "../api/policies.js";
import { hrefFor, navigate } from "../nav.js";
import "./policy/policy-findings.js";
import { loadPolicySchema, type PolicySchema } from "./policy/schema.js";

const COLUMNS: BnColumn[] = [
  { key: "kind", label: "Kind", width: "1px" },
  { key: "name", label: "Name" },
  { key: "priority", label: "Priority", width: "1px", align: "right" },
  { key: "mode", label: "Mode", width: "1px" },
  { key: "findings", label: "Findings", width: "1px" },
  { key: "updated", label: "Last modified", width: "1px" },
  { key: "by", label: "By", width: "1px" },
];

/** Keystroke debounce on the search box, so typing a table name is one query. */
const SEARCH_DEBOUNCE_MS = 300;

@customElement("page-policies")
export class PagePolicies extends LitElement {
  @property() instanceId = "";

  @state() private page: PolicyList | null = null;
  @state() private cursor: string | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;

  /** The form schema, for the kind and mode filters' labels and help. A schema
   * failure costs the descriptions, not the page. */
  @state() private schema: PolicySchema | null = null;

  @state() private kind: PolicyKind | "" = "";
  @state() private enforcement: EnforcementMode | "" = "";
  @state() private q = "";

  /** The draft-wide lint result, when the reader asked for one. */
  @state() private lint: { findings: PolicyFinding[]; blocks_publish: boolean } | null = null;
  @state() private linting = false;
  @state() private lintOpen = false;

  private searchTimer = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 24rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 70ch;
    }
    .head-actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      margin-bottom: 0;
    }
    .search {
      flex: 1 1 var(--bino-search-width);
      max-width: var(--bino-search-width-focus);
    }
    .filter {
      width: 14rem;
    }
    .toolbar-end {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-left: auto;
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .notices {
      display: grid;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
    }
    .notices p {
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-muted);
      max-width: 85ch;
    }
    .cell-name {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    .cell-name:hover {
      text-decoration: underline;
    }
    .cell-name:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .badges {
      display: flex;
      gap: var(--bino-space-xs);
      flex-wrap: wrap;
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    .verdict {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      max-width: 80ch;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.loadSchema();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.searchTimer);
  }

  /**
   * The list is loaded from `willUpdate` rather than from `connectedCallback`,
   * because the router sets `.instanceId` as a property and the element exists before
   * it arrives. Loading in both places would fetch the first page twice on every
   * navigation.
   */
  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId")) void this.load(null);
  }

  private async loadSchema() {
    try {
      const { schema } = await loadPolicySchema();
      this.schema = schema;
    } catch {
      this.schema = null;
    }
  }

  private async load(cursor: string | null) {
    if (!this.instanceId) return;
    this.loading = true;
    try {
      this.page = await listPolicies(this.instanceId, {
        kind: this.kind || undefined,
        enforcement: this.enforcement || undefined,
        q: this.q || undefined,
        cursor: cursor ?? undefined,
      });
      this.cursor = cursor;
      this.error = null;
    } catch (err) {
      this.error = parseError(err);
      this.page = null;
    } finally {
      this.loading = false;
    }
  }

  private async runLint() {
    this.linting = true;
    try {
      this.lint = await lintDraft(this.instanceId);
      this.lintOpen = true;
    } catch (err) {
      this.error = parseError(err);
    } finally {
      this.linting = false;
    }
  }

  private refilter() {
    void this.load(null);
  }

  private get filtered(): boolean {
    return this.q !== "" || this.kind !== "" || this.enforcement !== "";
  }

  private clearFilters() {
    this.q = "";
    this.kind = "";
    this.enforcement = "";
    this.refilter();
  }

  private editorHref(row: PolicyRow): string {
    return hrefFor("instances", this.instanceId, "policies", row.kind, row.name);
  }

  // ── Filter options ──────────────────────────────────────────────────────

  private get kindOptions(): BnSelectOption[] {
    const kinds = this.schema?.doc.kinds ?? [];
    if (kinds.length === 0) {
      return [
        { value: "", label: "Every kind" },
        ...POLICY_KINDS.map((k) => ({ value: k, label: k })),
      ];
    }
    return [
      { value: "", label: "Every kind" },
      ...kinds.map((k) => ({ value: k.kind, label: k.title || k.kind, description: k.help })),
    ];
  }

  private get modeOptions(): BnSelectOption[] {
    const options = this.schema?.options("EnforcementMode") ?? [
      { value: "Enforce", label: "Enforce" },
      { value: "Audit", label: "Audit" },
    ];
    return [{ value: "", label: "Every mode" }, ...options];
  }

  // ── Cells ───────────────────────────────────────────────────────────────

  /** The mode cell. A shadow policy — `Audit` or `DryRun` — is badged in the
   * decision-shadow tone, because it is evaluated and changes no outcome and that is
   * the difference an operator scans this column for. A disabled object is badged as
   * disabled whatever its mode says: it is out of the render entirely. */
  private renderMode(row: PolicyRow): TemplateResult | typeof nothing {
    if (!row.enabled) {
      return html`<bn-badge .variant=${enforcementModeVariant("Disabled")}>Disabled</bn-badge>`;
    }
    if (!row.enforcement_mode) {
      return html`<span class="sub">not evaluated per query</span>`;
    }
    return html`<div class="badges">
      <bn-badge .variant=${enforcementModeVariant(row.enforcement_mode)}>
        ${row.enforcement_mode}
      </bn-badge>
      ${row.shadow ? html`<span class="sub">changes no outcome</span>` : nothing}
    </div>`;
  }

  /** The findings cell: the worst rung with its rule id, linking into the editor,
   * which is where the finding is anchored to its field. */
  private renderFindings(row: PolicyRow): TemplateResult | typeof nothing {
    const f = row.findings ?? {};
    const errors = f.errors ?? 0;
    const blocking = f.blocking ?? 0;
    const warnings = f.warnings ?? 0;
    if (row.unreadable) {
      return html`<bn-badge variant="error">cannot decode</bn-badge>`;
    }
    if (errors + blocking + warnings === 0) return html`<span class="sub">—</span>`;
    const variant: BnBadgeVariant = errors > 0 ? "error" : blocking > 0 ? "warning" : "neutral";
    const label =
      errors > 0
        ? `${errors} blocking save`
        : blocking > 0
          ? `${blocking} blocking publish`
          : `${warnings} warning${warnings === 1 ? "" : "s"}`;
    const rules = (f.rules ?? []).join(", ");
    return html`<a class="cell-name" href=${this.editorHref(row)} title=${rules}>
      <bn-badge .variant=${variant}>${label}</bn-badge>
    </a>`;
  }

  private toRows(): BnRow[] {
    return (this.page?.items ?? []).map((row) => ({
      id: `${row.kind}/${row.name}`,
      kind: html`<span class="sub">${row.kind}</span>`,
      name: html`<a class="cell-name" href=${this.editorHref(row)}>${row.name}</a>
        ${row.change && row.change !== "unchanged"
          ? html`<span class="sub">${row.change} in this draft</span>`
          : nothing}`,
      priority: row.enforcement_mode ? String(row.priority) : html`<span class="sub">—</span>`,
      mode: this.renderMode(row),
      findings: this.renderFindings(row),
      updated: row.updated ? when(row.updated) : html`<span class="sub">—</span>`,
      by: row.updated_by_label || html`<span class="sub">—</span>`,
    }));
  }

  // ── States ──────────────────────────────────────────────────────────────

  /** The list-level notices, exactly as the API worded them. They are API rather
   * than UI copy so a CLI can print the same guidance, and the first one is the one
   * that explains why a working instance answers nothing. */
  private renderNotices(notices: PolicyNotice[]) {
    if (notices.length === 0) return nothing;
    return html`<div class="notices">
      ${notices.map(
        (n) => html`<bn-panel eyebrow=${noticeTitle(n.code)}>
          <p>${n.message}</p>
          ${n.code === "default_deny" || n.code === "empty_configuration"
            ? html`<div class="error-actions">
                <bn-button
                  variant="primary"
                  size="sm"
                  @click=${() => this.newPolicy("SqlAccessPolicy")}
                >
                  Create the first access policy
                </bn-button>
              </div>`
            : nothing}
        </bn-panel>`,
      )}
    </div>`;
  }

  private newPolicy(kind?: PolicyKind) {
    const base = hrefFor("instances", this.instanceId, "policies", "new");
    navigate(kind ? `${base}?kind=${encodeURIComponent(kind)}` : base);
  }

  private renderError(err: ApiError) {
    return html`<bn-card heading="Could not load the policy set">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ]}
        ></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p class="meta">
              The request did not reach Sluice Cloud, so there is no request id to quote.
            </p>`}
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load(this.cursor)}>
            Try again
          </bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  /** The two zero states, which must not look alike: nothing has been authored,
   * versus everything has been filtered out. */
  private renderEmpty() {
    if (this.filtered) {
      return html`<bn-empty-state
        icon="⌕"
        heading="No policy matches these filters"
        message="This instance may still hold policies — the current kind, mode or text filter excludes all of them. Free-text search matches the name and every catalog, schema, table, column and tag the object references."
      >
        <bn-button slot="actions" @click=${this.clearFilters}>Clear filters</bn-button>
      </bn-empty-state>`;
    }
    return html`<bn-empty-state
      icon="⊘"
      heading="No policies yet"
      message="With no SqlAccessPolicy granting access, this instance denies every query with ACL_DENIED and the reason “no SqlAccessPolicy matched (default-deny)”. That is the correct failure direction, and it is also the first thing that looks broken: nothing in the query's error names a policy, because no policy matched."
    >
      <bn-button slot="actions" variant="primary" @click=${() => this.newPolicy("SqlAccessPolicy")}>
        Create the first access policy
      </bn-button>
    </bn-empty-state>`;
  }

  private renderToolbar() {
    const total = this.page?.total ?? 0;
    const shown = this.page?.items.length ?? 0;
    return html`<div class="toolbar">
      <bn-field class="search" label="Search policies">
        <bn-input
          type="search"
          placeholder="Name, table, column or tag"
          .value=${this.q}
          @input=${(e: CustomEvent<{ value: string }>) => {
            this.q = e.detail.value;
            clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(() => this.refilter(), SEARCH_DEBOUNCE_MS);
          }}
        ></bn-input>
      </bn-field>
      <bn-field class="filter" label="Kind">
        <bn-select
          filterable
          placeholder="Every kind"
          .options=${this.kindOptions}
          .value=${this.kind}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            this.kind = e.detail.value as PolicyKind | "";
            this.refilter();
          }}
        ></bn-select>
      </bn-field>
      <bn-field class="filter" label="Enforcement mode">
        <bn-select
          placeholder="Every mode"
          .options=${this.modeOptions}
          .value=${this.enforcement}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            this.enforcement = e.detail.value as EnforcementMode | "";
            this.refilter();
          }}
        ></bn-select>
      </bn-field>
      <div class="toolbar-end">
        <span class="meta">
          ${total > 0 ? `${shown} of ${total}` : ""}
        </span>
        <bn-button size="sm" ?loading=${this.linting} @click=${() => void this.runLint()}>
          Check the draft
        </bn-button>
        <bn-button size="sm" ?loading=${this.loading} @click=${() => void this.load(this.cursor)}>
          Refresh
        </bn-button>
      </div>
    </div>`;
  }

  /** The draft-wide lint result. The verdict states the two rungs separately,
   * because a reader who cannot tell them apart cannot act on either. */
  private renderLintDrawer() {
    const result = this.lint;
    return html`<bn-drawer
      heading="Draft findings"
      .width=${640}
      ?open=${this.lintOpen}
      @close=${() => (this.lintOpen = false)}
    >
      ${result
        ? html`
            <p class="verdict">
              ${result.blocks_publish
                ? "This draft cannot be published as it stands. Every error blocks saving the object it is on; a blocking warning saves and stops the publish."
                : "Nothing in this draft blocks a publish. Warnings below block nothing and are there because something is about to be surprising."}
            </p>
            <policy-findings
              .findings=${result.findings}
              emptyText="The linter found nothing. It runs on every save as well as here, and the pinned Sluice binary's own verdict still wins whenever the two disagree."
            ></policy-findings>
          `
        : nothing}
    </bn-drawer>`;
  }

  override render() {
    return html`
      <header>
        <div class="head-text">
          <h1>Policies</h1>
          <p class="lede">
            The draft's policy objects, in the order Sluice will evaluate them: priority descending,
            then name ascending. Every "first wins" rule downstream inherits that order, so the list
            is not sorted alphabetically.
          </p>
        </div>
        <div class="head-actions">
          <bn-button variant="primary" @click=${() => this.newPolicy()}>New policy</bn-button>
        </div>
      </header>

      ${this.renderToolbar()}
      ${this.error
        ? this.renderError(this.error)
        : html`
            ${this.renderNotices(this.page?.notices ?? [])}
            <bn-card flush>
              <bn-table
                caption="Policy objects"
                clickable
                row-key="id"
                ?paginated=${(this.page?.items.length ?? 0) > 0}
                .columns=${COLUMNS}
                .rows=${this.toRows()}
                .nextCursor=${this.page?.next ?? null}
                ?loading=${this.loading}
                @row-click=${(e: CustomEvent<BnRowClickDetail>) => {
                  const row = this.page?.items[e.detail.index];
                  if (row) navigate(this.editorHref(row));
                }}
                @page-change=${(e: CustomEvent<BnPageChangeDetail>) => void this.load(e.detail.cursor)}
              >
                <div slot="empty">${this.renderEmpty()}</div>
              </bn-table>
            </bn-card>

            <!-- SC-F825. It reads exactly the objects this list holds, and "who can
                 reach what?" is the question a reader of a policy list is trying to
                 answer by scrolling it. Assembled on demand rather than on page load:
                 it is stamped with one generation, which is the correct semantics for
                 an audit artifact. -->
            <access-review .instanceId=${this.instanceId}></access-review>

            <!-- SC-F528. It sits under the review because the two are the same
                 question at two scales: the review answers "who can reach what
                 across this instance", this answers "what happens to THIS subject
                 on THESE tables, step by step". Advisory, and labelled so in its
                 own header — it gates nothing and the pinned binary decides. -->
            <policy-outcome .instanceId=${this.instanceId}></policy-outcome>
          `}
      ${this.renderLintDrawer()}
    `;
  }
}

/** noticeTitle is the eyebrow for one notice code. The message itself is the
 * server's; only the heading is ours. */
function noticeTitle(code: string): string {
  switch (code) {
    case "default_deny":
      return "Every query is denied";
    case "empty_configuration":
      return "Nothing is configured yet";
    case "shadow_only":
      return "Nothing is enforcing";
    default:
      return "Notice";
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-policies": PagePolicies;
  }
}
